// The front door (design/DIRECTION.md, "Landing"). Signed in → Home. Signed out: what Fellowship is,
// told the way a well-made book opens. The engraving and a verse for the day on the first page; then
// sections that stack like cards as you read (as the iOS app moves, FS-307): reading together, plans,
// sharing a verse, the library, and a calm, safe room; then one invitation. Honest copy only: no
// numbers, no testimonials, no claims the product can't keep.
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { m, useReducedMotion, useScroll, useTransform, type MotionValue } from "motion/react";
import { useEffect, useRef } from "react";
import { Art } from "../components/Art";
import { AvatarStack } from "../components/AvatarStack";
import { BookObject, type BookKind } from "../components/BookObject";
import { LinkButton } from "../components/Button";
import { Icon, type IconName } from "../components/Icon";
import { Logo } from "../components/Logo";
import { keys } from "../data/queries";
import { useTitle } from "../lib/useTitle";
import { Reveal } from "../motion/Reveal";
import { SplitText } from "../motion/SplitText";
import { StackDeck } from "../motion/StackDeck";
import s from "./landing.module.css";

export const Route = createFileRoute("/")({
  validateSearch: (q: Record<string, unknown>): { deleted?: boolean } => (q.deleted ? { deleted: true } : {}),
  beforeLoad: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData({ queryKey: keys.session, queryFn: () => context.source.session() });
    if (session?.onboarded) throw redirect({ to: "/home" });
  },
  component: Landing,
});

/** A verse for the day, chosen by the date from a short list (World English Bible, word for word). */
const VERSES = [
  { text: "God is our refuge and strength, a very present help in trouble.", ref: "Psalm 46:1" },
  { text: "Your word is a lamp to my feet, and a light for my path.", ref: "Psalm 119:105" },
  { text: "They are new every morning. Great is your faithfulness.", ref: "Lamentations 3:23" },
  { text: "The light shines in the darkness, and the darkness hasn’t overcome it.", ref: "John 1:5" },
  { text: "Open my eyes, that I may see wondrous things out of your law.", ref: "Psalm 119:18" },
  { text: "See how good and how pleasant it is for brothers to live together in unity!", ref: "Psalm 133:1" },
  { text: "The heavens declare the glory of God. The expanse shows his handiwork.", ref: "Psalm 19:1" },
  { text: "So teach us to count our days, that we may gain a heart of wisdom.", ref: "Psalm 90:12" },
  { text: "Therefore exhort one another, and build each other up, even as you also do.", ref: "1 Thessalonians 5:11" },
  { text: "One generation will commend your works to another, and will declare your mighty acts.", ref: "Psalm 145:4" },
];
function verseForToday() {
  const d = new Date();
  const day = Math.floor((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(d.getFullYear(), 0, 0)) / 86_400_000);
  return VERSES[day % VERSES.length]!;
}

const SHELF: { id: string; title: string; kind: BookKind }[] = [
  { id: "westminster-confession-of-faith", title: "The Westminster Confession of Faith", kind: "confession" },
  { id: "mcheyne-1842", title: "M’Cheyne’s Calendar for Daily Readings", kind: "book" },
  { id: "ante-nicene-fathers-vol-1", title: "Ante-Nicene Fathers, Volume I", kind: "book" },
  { id: "westminster-shorter-catechism", title: "The Westminster Shorter Catechism", kind: "confession" },
  { id: "1689-london-baptist-confession", title: "The 1677/89 London Baptist Confession of Faith", kind: "confession" },
  { id: "bcp-1871-daily", title: "The 1662 Daily Lectionary", kind: "book" },
  { id: "baptist-catechism", title: "The Baptist Catechism", kind: "confession" },
  { id: "beddome-exposition", title: "Beddome’s Exposition of the Baptist Catechism", kind: "book" },
];

/** The example friends in the previews: the prototype's people, named as examples. */
const FRIENDS = [
  { id: "u-ruth", displayName: "Ruth Adeyemi", avatarUrl: null },
  { id: "u-grace", displayName: "Grace Mensah", avatarUrl: null },
  { id: "u-samuel", displayName: "Samuel Hart", avatarUrl: null },
];

function Landing() {
  useTitle("");
  const { deleted } = Route.useSearch();
  const reduce = useReducedMotion();
  const verse = verseForToday();
  const hero = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: hero, offset: ["start start", "end start"] });
  const engravingY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const engravingRotate = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -4]);
  const verseY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  // Smooth scrolling here only: the front door is read top to bottom, the app is not (DIRECTION.md).
  useEffect(() => {
    if (reduce || !matchMedia("(pointer: fine)").matches) return;
    let stop: (() => void) | undefined;
    let live = true;
    void import("lenis").then(({ default: Lenis }) => {
      if (!live) return;
      const lenis = new Lenis({ duration: 1.05, easing: (t: number) => 1 - Math.pow(1 - t, 4), anchors: true });
      let raf = requestAnimationFrame(function frame(time) {
        lenis.raf(time);
        raf = requestAnimationFrame(frame);
      });
      stop = () => {
        cancelAnimationFrame(raf);
        lenis.destroy();
      };
    });
    return () => {
      live = false;
      stop?.();
    };
  }, [reduce]);

  return (
    <div className={s.landing}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className={s.nav}>
        <div className={s.navInner}>
          <Link to="/" className={s.brand} aria-label="Fellowship, by Bible Standard">
            <Logo size={1.875} wordSize={1.5} />
          </Link>
          <nav className={s.anchors} aria-label="On this page">
            <a href="#together">Reading together</a>
            <a href="#plans">Plans</a>
            <a href="#library">The library</a>
            <a href="#safe">A safe room</a>
          </nav>
          <LinkButton to="/sign-in" variant="secondary" size="sm">
            Sign in
          </LinkButton>
        </div>
      </header>

      <main id="main" tabIndex={-1} className={s.main}>
        {deleted && (
          <p className={s.notice} role="status">
            <Icon name="check" size={16} />
            Your account has been deleted.
          </p>
        )}

        <section className={s.hero} ref={hero} aria-labelledby="hero-title">
          <div className={s.heroWords}>
            <SplitText as="h1" id="hero-title" className={s.heroTitle} immediate>
              See what your friends are reading, and <em>answer them.</em>
            </SplitText>
            <p className={s.heroLede}>Share a verse with the people you know and talk it over. No strangers, no followers, no endless feed.</p>
            <div className={s.heroActions}>
              <LinkButton to="/sign-in" variant="primary" size="lg" arrow>
                Sign in or create an account
              </LinkButton>
              <a href="#together" className={s.quietLink}>
                How it works
                <Icon name="caret" size={14} />
              </a>
            </div>
          </div>
          <div className={s.heroPlate} aria-hidden="true">
            <span className={s.glow} />
            <m.span className={s.engraving} style={{ y: engravingY, rotate: engravingRotate }} />
          </div>
          <m.figure className={s.votd} style={{ y: verseY }} aria-label="A verse for today">
            <p className={s.votdHead}>
              <span>A verse for today</span>
              <time dateTime={new Date().toISOString().slice(0, 10)}>{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</time>
            </p>
            <blockquote>{verse.text}</blockquote>
            <figcaption>{verse.ref} · WEB</figcaption>
          </m.figure>
        </section>

        <StackDeck top={84}>
          <Together />
          <Plans />
          <ShareVerse />
          <Library />
          <Safe />
        </StackDeck>

        <section className={s.close} aria-labelledby="close-title">
          <span className={s.closeMark} aria-hidden="true" />
          <SplitText as="h2" id="close-title" className={s.closeTitle}>
            Come and read with your <em>friends.</em>
          </SplitText>
          <Reveal as="p" className={s.closeLede} delay={200}>
            An account takes a minute: a handle, your name, and an email code. Fellowship stays off until you turn it on.
          </Reveal>
          <Reveal delay={320}>
            <LinkButton to="/sign-in" variant="primary" size="lg" arrow>
              Create your account
            </LinkButton>
          </Reveal>
        </section>
      </main>

      <footer className={s.foot}>
        <Logo size={1.5} wordSize={1.25} />
        <p>A part of Bible Standard. Scripture from the World English Bible. Paintings from The Met’s open-access collection.</p>
        <nav aria-label="About Fellowship" className={s.footLinks}>
          <Link to="/rules">Community rule</Link>
          <Link to="/contact">Contact</Link>
          <a href="https://biblestandard.app">biblestandard.app</a>
          <a href="https://biblestandard.app/privacy">Privacy</a>
        </nav>
      </footer>
    </div>
  );
}

/* ── Reading together: a room in colour, words on glass, and what a friend's post looks like ── */
function Together() {
  return (
    <section id="together" className={`${s.section} ${s.together}`} aria-labelledby="together-title">
      <div className={s.room}>
        <Art name="friedrich" shape="none" decorative ratio="auto" className={s.roomArt} />
        <Reveal className={s.pane} variant="lift">
          <SplitText as="h2" id="together-title" className={s.sectionTitle}>
            A quiet room for <em>reading together.</em>
          </SplitText>
          <p className={s.sectionLede}>
            You see what your friends are reading and marking, and you answer them. Every post is anchored to a verse or a work, so the conversation always leads
            back to the text.
          </p>
        </Reveal>
      </div>
      <Reveal className={s.example} variant="rise" delay={120} aria-label="An example of a post">
        <article className={s.post}>
          <header className={s.postHead}>
            <span className={s.face} aria-hidden="true">
              RA
            </span>
            <span>
              <strong>Ruth Adeyemi</strong> <span className={s.muted}>shared a verse</span>
            </span>
          </header>
          <p className={s.ref}>Romans 8:28 · WEB</p>
          <blockquote className={s.postQuote}>We know that all things work together for good for those who love God, for those who are called according to his purpose.</blockquote>
          <p className={s.postBody}>Read this at the hospital this morning. Holding on to “all”.</p>
          <p className={s.postActions}>
            <span>
              <Icon name="amen" size={18} /> Amen
            </span>
            <span>
              <Icon name="praying" size={18} /> Praying
            </span>
            <span>
              <Icon name="comment" size={18} /> Reply
            </span>
          </p>
        </article>
        <p className={s.caption}>An example. Words, not emoji, and no counts to chase.</p>
      </Reveal>
    </section>
  );
}

/* ── Plans: the rubric as a whole ground ── */
function Plans() {
  const rows: { title: string; day: string; who: string; read: number }[] = [
    { title: "John in 21 Days", day: "Day 5 · John 5", who: "Ruth has read today", read: 1 },
    { title: "M’Cheyne’s Calendar", day: "14 October · four readings", who: "Grace and Samuel have read today", read: 2 },
  ];
  return (
    <section id="plans" className={`${s.section} ${s.plans}`} aria-labelledby="plans-title">
      <div className={s.plansGrid}>
        <div>
          <SplitText as="h2" id="plans-title" className={s.sectionTitle}>
            Read a plan with the people you <em>love.</em>
          </SplitText>
          <p className={s.sectionLede}>
            M’Cheyne, the 1662 lectionary, a gospel in three weeks, or a plan of your own. Read alone or together, talk about each day, and see who has read. Never a
            score, never a streak.
          </p>
        </div>
        <Reveal stagger className={s.planList}>
          {rows.map((r) => (
            <div key={r.title} className={s.planRow}>
              <span className={s.planBoard} aria-hidden="true">
                {r.title[0]}
              </span>
              <span className={s.planWords}>
                <strong>{r.title}</strong>
                <span>{r.day}</span>
                <span className={s.presence}>
                  <AvatarStack users={FRIENDS.slice(0, r.read)} size={22} />
                  {r.who}
                </span>
              </span>
            </div>
          ))}
          <Art name="ruisdael" treatment="duotone" shape="arch" decorative ratio="5 / 3" className={s.planArt} />
        </Reveal>
      </div>
    </section>
  );
}

/* ── Sharing a verse: the words light up as you read down, as choosing words to quote does ── */
function ShareVerse() {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 85%", "end 45%"] });
  const words = "God is our refuge and strength, a very present help in trouble.".split(" ");
  return (
    <section id="share" className={`${s.section} ${s.share}`} aria-labelledby="share-title">
      <div className={s.shareInner}>
        <h2 id="share-title" className={s.shareTitle}>
          Share the words that stopped you.
        </h2>
        <div ref={ref} className={s.scrub}>
          <p className={s.scrubText} aria-label="God is our refuge and strength, a very present help in trouble.">
            {words.map((w, i) => (
              <ScrubWord key={i} word={w} i={i} n={words.length} progress={scrollYProgress} still={!!reduce} />
            ))}
          </p>
          <p className={s.scrubRef}>Psalm 46:1 · WEB</p>
        </div>
        <ol className={s.steps}>
          <Step icon="search" title="Type a reference" text="“Rom 8 28” is enough. The passage appears as you type." />
          <Step icon="highlight" title="Choose the words" text="Tap the words to quote; the rest is set aside with an ellipsis." />
          <Step icon="people" title="Add a line" text="Say why it mattered. Only your friends will see it." />
        </ol>
      </div>
    </section>
  );
}

function ScrubWord({ word, i, n, progress, still }: { word: string; i: number; n: number; progress: MotionValue<number>; still: boolean }) {
  const start = (i / n) * 0.8;
  const opacity = useTransform(progress, [start, start + 0.2], [0.18, 1]);
  return (
    <>
      <m.span aria-hidden="true" style={{ opacity: still ? 1 : opacity }}>
        {word}
      </m.span>{" "}
    </>
  );
}

function Step({ icon, title, text }: { icon: IconName; title: string; text: string }) {
  return (
    <li className={s.step}>
      <span className={s.stepGlyph} aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <strong>{title}</strong>
      <span>{text}</span>
    </li>
  );
}

/* ── The library: books as objects on a ledge ── */
function Library() {
  return (
    <section id="library" className={`${s.section} ${s.library}`} aria-labelledby="library-title">
      <div className={s.libraryHead}>
        <SplitText as="h2" id="library-title" className={s.sectionTitle}>
          The Bible, and the library <em>beside it.</em>
        </SplitText>
        <p className={s.sectionLede}>
          The confessions and catechisms, the Fathers and the old calendars, free to read. Highlight and note as you go, and share a passage with its source attached.
        </p>
      </div>
      <Reveal stagger className={s.shelf} aria-label="Some of the library">
        {SHELF.map((b) => (
          <BookObject key={b.id} src={`/covers/${b.id}.webp`} title={b.title} kind={b.kind} height={210} />
        ))}
      </Reveal>
    </section>
  );
}

/* ── A calm and safe room ── */
function Safe() {
  const promises: { icon: IconName; title: string; text: string }[] = [
    { icon: "people", title: "Friends only", text: "Mutual, by request. No followers, no public profiles, no strangers in your feed." },
    { icon: "shield", title: "Safe from the first day", text: "Report or block in two taps. Reports are read by people, and blocking hides everything both ways." },
    { icon: "lock", title: "Nothing to perform", text: "No streaks, no badges, no ranking. The feed ends when you are caught up." },
    { icon: "undo", title: "Yours to leave", text: "Turn Fellowship off and nothing is lost. Download everything, or delete it all." },
  ];
  return (
    <section id="safe" className={`${s.section} ${s.safe}`} aria-labelledby="safe-title">
      <div className={s.safeGrid}>
        <Art name="latour" shape="arch" decorative ratio="4 / 5" className={s.safeArt} />
        <div>
          <SplitText as="h2" id="safe-title" className={s.sectionTitle}>
            A calm room, <em>kept safe.</em>
          </SplitText>
          <Reveal as="ul" stagger className={s.promises}>
            {promises.map((p) => (
              <li key={p.title}>
                <span className={s.stepGlyph} aria-hidden="true">
                  <Icon name={p.icon} size={20} />
                </span>
                <span>
                  <strong>{p.title}</strong>
                  <span>{p.text}</span>
                </span>
              </li>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
